import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { IPromptProfile } from '../../types/prompt-profile';
import { BullhornEntityType } from '../../types/bullhorn/bullhorn-entity-type';
import { IClientConfig } from '../../types/client-config';

@Component({
    selector: 'app-prompt-carousel',
    template: `
    <div class="slider" (click)="slider.focus()">
      <div
        class="slides"
        (keydown.enter)="clickPrompt()"
        (keydown.space)="clickPrompt()"
        tabindex="0"
        (keydown.arrowLeft)="handlePrevious()"
        (keydown.arrowRight)="handleNext()"
        #slider
        >
        @for (promptProfile of availablePromptProfiles; track promptProfile; let i = $index) {
          <app-prompt-paragraph
            id="{{ i }}"
            [ngClass]="{ active: i === current }"
            [promptProfile]="promptProfile"
            [clientConfig]="clientConfig"
            [entityType]="entityType"
          ></app-prompt-paragraph>
        }
      </div>
      @if (availablePromptProfiles.length > 1) {
        <footer>
          <mat-icon fontIcon="arrow_back_ios" class="material-icons-outlined" (click)="handlePrevious()"></mat-icon>
          <div class="slide-indicators">
            @for (promptProfiles of availablePromptProfiles; track promptProfiles; let i = $index) {
              <span
                class="indicator"
                [ngClass]="{ active: i === current }"
                (click)="handleSlideIndicatorClick(i)"
              ></span>
            }
          </div>
          <mat-icon fontIcon="arrow_forward_ios" class="material-icons-outlined" (click)="handleNext()"></mat-icon>
        </footer>
      }
    </div>
    `,
    styleUrls: [`./prompt-carousel.scss`],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PromptCarouselComponent {
  @Input() availablePromptProfiles!: IPromptProfile[];
  @Input() entityType!: BullhornEntityType;
  @Input() clientConfig!: IClientConfig;
  @Output() selectedPrompt = new EventEmitter();
  @Output() clickedPrompt = new EventEmitter();
  current = 0;

  @ViewChild('slider') private slider: ElementRef | undefined;

  handleNext() {
    if (this.current >= this.availablePromptProfiles.length - 1) {
      this.current = 0;
    } else {
      this.current++;
    }

    this.setCurrent();
  }

  handlePrevious() {
    if (this.current == 0) {
      this.current = this.availablePromptProfiles.length - 1;
    } else {
      this.current--;
    }

    this.setCurrent();
  }

  handleSlideIndicatorClick(index: number) {
    this.current = index;
    this.setCurrent();
  }

  clickPrompt(): void {
    this.clickedPrompt.emit(this.availablePromptProfiles[this.current]);
  }

  private setCurrent(): void {
    setTimeout(() => {
      // Necessary for rapidly navigating the carousel with the keyboard
      this.selectedPrompt.emit(this.availablePromptProfiles[this.current]);
      this.scrollTo();
    }, 0);
  }

  private scrollTo() {
    const element = document.getElementById(this.current.toString());

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    }
    this.slider!.nativeElement.focus();
  }
}
